import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  draftScore,
  getTierList,
  suggestBan,
  suggestPick,
  suggestPickAny,
  Role,
  SuggestBanItem,
  SuggestItem,
  ROLES,
} from "./proApi";
import {
  championDisplayName,
  iconFallbackUrl,
  iconUrl,
  splashFallbackUrl,
  splashUrl,
  swapToFallback,
} from "./championAssets";
import { ChampionDatalist, pctColor, useProIndex } from "./shared";

const MAX_BANS = 5;

// Dscore delta -> win-probability points: slope of the combined-model sigmoid
// (PRODUCT_SCALE = 16.35) at an even draft, x100 for percentage points.
const WIN_POINTS_PER_DSCORE = (16.35 / 4) * 100;

type Team = "blue" | "red";
type Panel =
  | { kind: "idle" }
  | { kind: "picks"; slot: number; model: string | null; items: SuggestItem[] }
  | {
      kind: "teampicks";
      team: Team;
      model: string | null;
      items: SuggestBanItem[];
    }
  | { kind: "bans"; team: Team; items: SuggestBanItem[] };

function slotTeam(slot: number): Team {
  return slot < 5 ? "blue" : "red";
}

// ---------------------------------------------------------------------------

function SlotCard({
  slot,
  champion,
  isTarget,
  banned,
  onChange,
  onSuggest,
  champions,
  datalistId,
}: {
  slot: number;
  champion: string;
  isTarget: boolean;
  banned: boolean;
  onChange: (v: string) => void;
  onSuggest: () => void;
  champions: string[];
  datalistId: string;
}) {
  const team = slotTeam(slot);
  const role = ROLES[slot % 5];
  const resolved = champion ? championDisplayName(champion) : "";
  const splash = resolved && !banned ? splashUrl(resolved) : null;
  const splashFb = resolved && !banned ? splashFallbackUrl(resolved) : null;
  const known =
    !banned &&
    champions.some((c) => c.toLowerCase() === champion.trim().toLowerCase());

  return (
    <div
      className={`pro-draft__slot pro-draft__slot--${team}${
        isTarget ? " is-target" : ""
      }`}
    >
      {splash && (
        <div
          className="pro-draft__slot-splash"
          style={{
            backgroundImage: `url(${splash}), url(${splashFb})`,
          }}
        />
      )}
      <div className="pro-draft__slot-shade" />
      <div className="pro-draft__slot-body">
        <span className="pro-draft__slot-role">{role}</span>
        <div className="pro-draft__slot-main">
          {champion && known ? (
            <span className="pro-draft__slot-name">{resolved}</span>
          ) : (
            <>
              <input
                className="pro-draft__slot-input"
                type="text"
                list={datalistId}
                value={champion}
                placeholder="—"
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => onChange(e.target.value)}
              />
              {banned && <span className="pro-draft__slot-warn">banned</span>}
            </>
          )}
        </div>
        <div className="pro-draft__slot-actions">
          {champion && (
            <button
              type="button"
              className="pro-draft__slot-btn"
              title="Clear"
              onClick={() => onChange("")}
            >
              ✕
            </button>
          )}
          <button
            type="button"
            className="pro-draft__slot-btn"
            title="Suggest picks for this slot"
            onClick={onSuggest}
          >
            ✦
          </button>
        </div>
      </div>
    </div>
  );
}

function BanStrip({
  team,
  bans,
  onRemove,
  onAdd,
  onSuggest,
  champions,
  datalistId,
}: {
  team: Team;
  bans: string[];
  onRemove: (name: string) => void;
  onAdd: (name: string) => void;
  onSuggest: () => void;
  champions: string[];
  datalistId: string;
}) {
  const [value, setValue] = useState("");
  const full = bans.length >= MAX_BANS;
  return (
    <div className={`pro-draft__bans pro-draft__bans--${team}`}>
      <div className="pro-draft__bans-row">
        {Array.from({ length: MAX_BANS }, (_, i) => {
          const name = bans[i];
          const icon = name ? iconUrl(championDisplayName(name)) : null;
          const fb = name ? iconFallbackUrl(championDisplayName(name)) : null;
          return (
            <div
              key={i}
              className={`pro-draft__ban-slot${name ? " is-filled" : ""}`}
              title={name ? `${name} — click to unban` : "Empty ban slot"}
              onClick={() => name && onRemove(name)}
              onKeyDown={(e) => {
                if (name && (e.key === "Enter" || e.key === " "))
                  onRemove(name);
              }}
              role={name ? "button" : undefined}
              tabIndex={name ? 0 : undefined}
            >
              {icon && (
                <img
                  src={icon}
                  alt={name}
                  onError={(e) => swapToFallback(e, fb)}
                />
              )}
            </div>
          );
        })}
      </div>
      <input
        type="text"
        list={datalistId}
        value={value}
        disabled={full}
        placeholder={full ? "Full" : "Ban…"}
        autoComplete="off"
        spellCheck={false}
        style={{ width: 92 }}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            onAdd(value.trim());
            setValue("");
          }
        }}
        onBlur={() => {
          const match = champions.find(
            (c) => c.toLowerCase() === value.trim().toLowerCase(),
          );
          if (match) {
            onAdd(match);
            setValue("");
          }
        }}
      />
      <button
        type="button"
        className="pro__btn pro-draft__bans-btn"
        onClick={onSuggest}
        title="Suggest the biggest threats to ban"
      >
        Suggest
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Suggester() {
  const { champions } = useProIndex();
  const [board, setBoard] = useState<string[]>(Array(10).fill(""));
  const [bans, setBans] = useState<{ blue: string[]; red: string[] }>({
    blue: [],
    red: [],
  });
  const [meta, setMeta] = useState(true);
  const [weeks, setWeeks] = useState(2);
  const [minRecent, setMinRecent] = useState(0);
  const [actIdx, setActIdx] = useState<Record<string, number>>({});
  const [panel, setPanel] = useState<Panel>({ kind: "idle" });
  const [panelLoading, setPanelLoading] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [targetSlot, setTargetSlot] = useState<number | null>(null);

  const [score, setScore] = useState<{
    dscore: number;
    pBlue: number;
  } | null>(null);
  const [filled, setFilled] = useState(0);
  const scoreSeq = useRef(0);

  const allBans = useMemo(() => [...bans.blue, ...bans.red], [bans]);

  // a banned name typed in a slot is never a pick: treat it as empty
  const effBoard = useMemo(
    () =>
      board.map((c) =>
        allBans.some((b) => b.toLowerCase() === c.trim().toLowerCase())
          ? ""
          : c,
      ),
    [board, allBans],
  );

  // games per champion-role over the selected window, to filter suggestions
  useEffect(() => {
    let cancelled = false;
    getTierList({ kind: "self", min_recent: 0, min_n: 0, weeks })
      .then((d) => {
        if (cancelled) return;
        const idx: Record<string, number> = {};
        d.items.forEach((it) => {
          idx[`${(it.champ || "").toLowerCase()}|${it.role}`] =
            it.n_window ?? it.n_recent;
        });
        setActIdx(idx);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [weeks]);

  const activityOf = useCallback(
    (champion: string, role: string) =>
      actIdx[`${championDisplayName(champion).toLowerCase()}|${role}`] ?? 0,
    [actIdx],
  );

  const setSlot = useCallback((slot: number, value: string) => {
    setBoard((prev) => {
      const next = [...prev];
      next[slot] = value;
      return next;
    });
  }, []);

  // ---- live score of the complete draft (merged model, like Drafter.one) ----
  useEffect(() => {
    const known = (c: string) =>
      champions.some((n) => n.toLowerCase() === c.trim().toLowerCase());
    const filledCount = effBoard.filter((c) => c && known(c)).length;
    setFilled(filledCount);
    if (filledCount < 10) {
      setScore(null);
      return undefined;
    }
    scoreSeq.current += 1;
    const seq = scoreSeq.current;
    const t = setTimeout(() => {
      draftScore({ blue: effBoard.slice(0, 5), red: effBoard.slice(5) })
        .then((d) => {
          if (seq !== scoreSeq.current) return;
          if (d.complete && d.dscore != null && d.p_blue != null) {
            setScore({ dscore: d.dscore, pBlue: d.p_blue });
          }
        })
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [effBoard, champions]);

  // ---- suggestions ----
  const runPickSuggest = useCallback(
    (slot: number) => {
      setTargetSlot(slot);
      setPanelLoading(true);
      setPanelError(null);
      const b = [...effBoard.slice(0, 5)];
      const r = [...effBoard.slice(5)];
      (slot < 5 ? b : r)[slot % 5] = ""; // re-suggest works on a filled slot too
      suggestPick({
        blue: b,
        red: r,
        slot,
        bans: allBans,
        meta,
        top_n: 40,
        meta_days: weeks * 7,
        meta_min_picks: Math.max(1, minRecent),
      })
        .then((d) =>
          setPanel({
            kind: "picks",
            slot,
            model: d.model?.name ?? null,
            items: d.items,
          }),
        )
        .catch((e) => setPanelError(e.message || "Error"))
        .finally(() => setPanelLoading(false));
    },
    [effBoard, allBans, meta, weeks, minRecent],
  );

  const runTeamSuggest = useCallback(
    (team: Team) => {
      setTargetSlot(null);
      setPanelLoading(true);
      setPanelError(null);
      suggestPickAny({
        blue: effBoard.slice(0, 5),
        red: effBoard.slice(5),
        team,
        bans: allBans,
        meta,
        top_n: 40,
        meta_days: weeks * 7,
        meta_min_picks: Math.max(1, minRecent),
      })
        .then((d) =>
          setPanel({
            kind: "teampicks",
            team,
            model: d.model?.name ?? null,
            items: d.items,
          }),
        )
        .catch((e) => setPanelError(e.message || "Error"))
        .finally(() => setPanelLoading(false));
    },
    [effBoard, allBans, meta, weeks, minRecent],
  );

  const runBanSuggest = useCallback(
    (team: Team) => {
      setTargetSlot(null);
      setPanelLoading(true);
      setPanelError(null);
      suggestBan({
        blue: effBoard.slice(0, 5),
        red: effBoard.slice(5),
        team,
        bans: allBans,
        meta,
        top_n: 40,
        meta_days: weeks * 7,
        meta_min_picks: Math.max(1, minRecent),
      })
        .then((d) => setPanel({ kind: "bans", team, items: d.items }))
        .catch((e) => setPanelError(e.message || "Error"))
        .finally(() => setPanelLoading(false));
    },
    [effBoard, allBans, meta, weeks, minRecent],
  );

  const addBan = useCallback(
    (team: Team, name: string): boolean => {
      const display = championDisplayName(name);
      if (bans[team].length >= MAX_BANS) return false;
      if (allBans.some((b) => b.toLowerCase() === display.toLowerCase()))
        return false;
      // a picked champion cannot be banned
      if (board.some((c) => c.trim().toLowerCase() === display.toLowerCase()))
        return false;
      setBans((prev) => ({ ...prev, [team]: [...prev[team], display] }));
      return true;
    },
    [bans, allBans, board],
  );

  // Re-run the open ban panel after a ban lands (bans state is async, so via effect)
  const pendingBanRerun = useRef<Team | null>(null);
  useEffect(() => {
    if (pendingBanRerun.current) {
      const t = pendingBanRerun.current;
      pendingBanRerun.current = null;
      runBanSuggest(t);
    }
  }, [bans, runBanSuggest]);

  const reset = useCallback(() => {
    setBoard(Array(10).fill(""));
    setBans({ blue: [], red: [] });
    setPanel({ kind: "idle" });
    setTargetSlot(null);
    setScore(null);
  }, []);

  // ---- render ----
  const pBlue = score ? score.pBlue : null;

  const panelTitle = (() => {
    if (panelLoading) return "Computing…";
    if (panel.kind === "picks") {
      const team = slotTeam(panel.slot);
      return `Best picks — ${team === "blue" ? "Blue" : "Red"} ${
        ROLES[panel.slot % 5]
      }${panel.model ? ` · ${panel.model}` : ""}`;
    }
    if (panel.kind === "teampicks") {
      return `Best picks — ${panel.team === "blue" ? "Blue" : "Red"}, any role${
        panel.model ? ` · ${panel.model}` : ""
      }`;
    }
    if (panel.kind === "bans") {
      return `Recommended bans for ${
        panel.team === "blue" ? "Blue" : "Red"
      } — biggest threats on the ${
        panel.team === "blue" ? "Red" : "Blue"
      } side`;
    }
    return "Suggestions";
  })();

  const renderSuggRow = (
    key: string,
    name: string,
    value: number,
    range: { min: number; max: number },
    why: string,
    extra: React.ReactNode,
    onClick: () => void,
    opts: { warn?: React.ReactNode; compact?: boolean } = {},
  ) => {
    const display = championDisplayName(name);
    const splash = splashUrl(display);
    const splashFb = splashFallbackUrl(display);
    // bar: relative to the DISPLAYED list (visual comparison only)
    const span = range.max - range.min;
    const fit = span > 1e-12 ? ((value - range.min) / span) * 100 : 100;
    // number: ABSOLUTE score in win-probability points (signed)
    const pts = value * WIN_POINTS_PER_DSCORE;
    let ptsColor = "#e06c6c";
    if (pts >= 0.5) ptsColor = "#52c481";
    else if (pts >= 0) ptsColor = "#d9a441";
    return (
      <div
        key={key}
        className={`pro-draft__sugg-row${
          opts.compact ? " pro-draft__sugg-row--compact" : ""
        }`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        data-tip={opts.compact ? why.replace(/méta/g, "meta") : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter") onClick();
        }}
      >
        <div
          className="pro-draft__sugg-splash"
          style={
            splash
              ? { backgroundImage: `url(${splash}), url(${splashFb})` }
              : undefined
          }
        />
        <div className="pro-draft__sugg-body">
          <span className="pro-draft__sugg-name">{display}</span>
          {extra}
          {opts.warn}
          <div className="pro__bar-track pro-draft__sugg-bar">
            <div
              className="pro__bar-fill"
              style={{ width: `${fit}%`, background: pctColor(fit) }}
            />
          </div>
          <span
            className="pro-draft__rating"
            data-tip="Estimated impact on win probability, in percentage points, assuming the rest of the draft follows the meta expectation (in blind mode: after the opponent's best single counter-response). Approximate scale — negative means the pick concedes an edge; in blind mode with an activity filter, most realistic picks are slightly negative."
            style={{ color: ptsColor }}
          >
            {pts >= 0 ? "+" : ""}
            {pts.toFixed(1)}
          </span>
          {!opts.compact && (
            <span className="pro__why">{why.replace(/méta/g, "meta")}</span>
          )}
        </div>
      </div>
    );
  };

  const warnBadge = (it: SuggestItem | SuggestBanItem) => {
    const c = it.counter;
    if (!c) return null;
    const pts = c.delta * WIN_POINTS_PER_DSCORE;
    if (pts > -1.0) return null;
    return (
      <span
        className="pro-draft__warn"
        data-tip={`Vulnerable: if the opponent answers with ${c.champion} (${
          c.role
        }), this pick swings by ${pts.toFixed(1)} pts.`}
      >
        ⚠
      </span>
    );
  };

  const renderDualLists = (
    all: (SuggestItem & { role?: Role })[],
    roleOf: (it: SuggestItem & { role?: Role }) => Role,
    extraOf: (it: SuggestItem & { role?: Role }) => React.ReactNode,
    onPick: (it: SuggestItem & { role?: Role }) => void,
  ) => {
    const filtered = all.filter(
      (it) => activityOf(it.champion, roleOf(it)) >= minRecent,
    );
    if (!filtered.length)
      return (
        <div className="pro__empty">
          No suggestion above the activity filter — lower it.
        </div>
      );
    const expected = [...filtered]
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
    const blind = [...filtered]
      .sort((a, b) => b.value_safe - a.value_safe)
      .slice(0, 12);
    const rangeOf = (items: SuggestItem[], f: (it: SuggestItem) => number) => ({
      min: Math.min(...items.map(f)),
      max: Math.max(...items.map(f)),
    });
    const rExp = rangeOf(expected, (it) => it.value);
    const rBlind = rangeOf(blind, (it) => it.value_safe);
    return (
      <div className="pro-draft__dual">
        <div>
          <div
            className="pro-draft__dual-h"
            data-tip="Ranked by expected value: the rest of the draft is assumed to follow the meta expectation. ⚠ flags picks that a single meta counter-pick would swing by 1+ win point."
          >
            Expected
          </div>
          {expected.map((it) =>
            renderSuggRow(
              `e|${it.champion}|${roleOf(it)}`,
              it.champion,
              it.value,
              rExp,
              it.why,
              extraOf(it),
              () => onPick(it),
              { compact: true, warn: warnBadge(it) },
            ),
          )}
        </div>
        <div>
          <div
            className="pro-draft__dual-h"
            data-tip="Blind-pick ranking: expected value PLUS the single worst counter-response the opponent could still pick from the current meta. Hover a row for the counter."
          >
            Blind-safe
          </div>
          {blind.map((it) =>
            renderSuggRow(
              `s|${it.champion}|${roleOf(it)}`,
              it.champion,
              it.value_safe,
              rBlind,
              it.counter
                ? `counter risk: ${it.counter.champion} (${it.counter.role}) ${(
                    it.counter.delta * WIN_POINTS_PER_DSCORE
                  ).toFixed(1)} pts — ${it.why}`
                : it.why,
              extraOf(it),
              () => onPick(it),
              { compact: true },
            ),
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pro__page">
      <div className="pro__page-head">
        <h1>Draft suggester</h1>
      </div>
      <p className="pro__page-sub">
        Fill in the locked picks on both sides, set the bans (up to 5 per team),
        then ask for suggestions on any empty slot — each side is rated by{" "}
        <b>its own model</b>, while the full-draft score uses the{" "}
        <b>combined model</b>, exactly like the public Drafter.one analysis.
      </p>

      {/* Score bar */}
      <div className="pro-draft__scorebar">
        <div className="pro-draft__score-side">
          <span className="pro-draft__score-team is-blue">Blue</span>
          <span className="pro-draft__score-pct" style={{ color: "#3b82d6" }}>
            {pBlue == null ? "—" : `${(pBlue * 100).toFixed(1)}%`}
          </span>
        </div>
        <div className="pro-draft__score-center">
          {score ? (
            <>
              <b>
                {Math.abs(score.pBlue - 0.5) < 0.0005
                  ? "Even draft"
                  : `${score.pBlue >= 0.5 ? "Blue" : "Red"} +${(
                      Math.abs(score.pBlue - 0.5) * 100
                    ).toFixed(1)} pts`}
              </b>
              combined model · full draft
            </>
          ) : (
            <>
              <b>{filled}/10</b>
              fill all slots to score the draft
            </>
          )}
        </div>
        <div className="pro-draft__score-side pro-draft__score-side--red">
          <span className="pro-draft__score-team is-red">Red</span>
          <span className="pro-draft__score-pct" style={{ color: "#d64545" }}>
            {pBlue == null ? "—" : `${((1 - pBlue) * 100).toFixed(1)}%`}
          </span>
        </div>
        <div className="pro-draft__score-track">
          <div
            className="pro-draft__score-blue"
            style={{ width: `${(pBlue ?? 0.5) * 100}%` }}
          />
          <div className="pro-draft__score-red" />
        </div>
      </div>

      {/* Board */}
      <div className="pro-draft__board">
        <div className="pro-draft__team">
          <div className="pro-draft__team-head pro-draft__team-head--blue">
            <h2>Blue side</h2>
            <button
              type="button"
              className="pro__btn"
              data-tip="Best picks for Blue across all its empty roles."
              onClick={() => runTeamSuggest("blue")}
            >
              ✦ Best pick
            </button>
          </div>
          {[0, 1, 2, 3, 4].map((slot) => (
            <SlotCard
              key={slot}
              slot={slot}
              champion={board[slot]}
              isTarget={targetSlot === slot}
              banned={allBans.some(
                (b) => b.toLowerCase() === board[slot].trim().toLowerCase(),
              )}
              onChange={(v) => setSlot(slot, v)}
              onSuggest={() => runPickSuggest(slot)}
              champions={champions}
              datalistId="pro-sg-champions"
            />
          ))}
          <BanStrip
            team="blue"
            bans={bans.blue}
            onRemove={(name) =>
              setBans((prev) => ({
                ...prev,
                blue: prev.blue.filter((b) => b !== name),
              }))
            }
            onAdd={(name) => addBan("blue", name)}
            onSuggest={() => runBanSuggest("blue")}
            champions={champions}
            datalistId="pro-sg-champions"
          />
        </div>

        <div className="pro-draft__center">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <label className="pro-draft__meta-toggle" htmlFor="pro-sg-meta">
              <input
                id="pro-sg-meta"
                type="checkbox"
                checked={meta}
                onChange={(e) => setMeta(e.target.checked)}
              />
              Meta completion — score empty slots against the current
              competitive meta
            </label>
            <label
              className="pro__field"
              htmlFor="pro-sg-minrecent"
              data-tip="Drives BOTH the display filter (suggestions below the threshold are hidden) and the meta pool used server-side: probable picks for empty slots — expectations and worst-counter search — are champions with at least this many games over the window."
            >
              Min
              <input
                id="pro-sg-minrecent"
                type="number"
                min={0}
                value={minRecent}
                onChange={(e) =>
                  setMinRecent(Math.max(0, parseInt(e.target.value || "0", 10)))
                }
              />
              games in the last
              <input
                id="pro-sg-weeks"
                type="number"
                min={1}
                max={520}
                value={weeks}
                data-tip="Size of the recent window, in weeks."
                onChange={(e) =>
                  setWeeks(
                    Math.max(
                      1,
                      Math.min(520, parseInt(e.target.value || "1", 10)),
                    ),
                  )
                }
              />
              weeks
            </label>
            <button type="button" className="pro__btn" onClick={reset}>
              Reset
            </button>
          </div>

          <div className="pro__card pro-draft__suggestions">
            <div className="pro__card-title">{panelTitle}</div>
            {panelError && <div className="pro__empty">{panelError}</div>}
            {panelLoading && <div className="pro__empty">Computing…</div>}
            {!panelLoading && !panelError && panel.kind === "idle" && (
              <div className="pro__empty">
                Click ✦ on a slot for pick suggestions, or “Suggest” next to a
                ban row for the best bans.
              </div>
            )}
            {!panelLoading &&
              !panelError &&
              panel.kind === "picks" &&
              renderDualLists(
                panel.items,
                () => ROLES[panel.slot % 5],
                () => null,
                (it) => {
                  setSlot(panel.slot, championDisplayName(it.champion));
                  setTargetSlot(null);
                  setPanel({ kind: "idle" });
                },
              )}
            {!panelLoading &&
              !panelError &&
              panel.kind === "teampicks" &&
              (panel.items.length ? (
                renderDualLists(
                  panel.items,
                  (it) => it.role as Role,
                  (it) => <span className="pro__role">{it.role}</span>,
                  (it) => {
                    setSlot(
                      (it as SuggestBanItem).slot,
                      championDisplayName(it.champion),
                    );
                    setPanel({ kind: "idle" });
                  },
                )
              ) : (
                <div className="pro__empty">
                  No empty slot left on this side.
                </div>
              ))}
            {!panelLoading &&
              !panelError &&
              panel.kind === "bans" &&
              (() => {
                if (!panel.items.length)
                  return (
                    <div className="pro__empty">
                      No threat left to ban — the opposing board is full.
                    </div>
                  );
                const items = panel.items
                  .filter((it) => activityOf(it.champion, it.role) >= minRecent)
                  .slice(0, 12);
                if (!items.length)
                  return (
                    <div className="pro__empty">
                      No suggestion above the activity filter — lower it.
                    </div>
                  );
                const range = {
                  min: Math.min(...items.map((x) => x.value)),
                  max: Math.max(...items.map((x) => x.value)),
                };
                return (
                  <div>
                    {items.map((it) =>
                      renderSuggRow(
                        `${it.champion}|${it.role}`,
                        it.champion,
                        it.value,
                        range,
                        it.why,
                        <span className="pro__role">{it.role}</span>,
                        () => {
                          if (addBan(panel.team, it.champion)) {
                            pendingBanRerun.current = panel.team;
                          }
                        },
                      ),
                    )}
                  </div>
                );
              })()}
          </div>
        </div>

        <div className="pro-draft__team">
          <div className="pro-draft__team-head pro-draft__team-head--red">
            <h2>Red side</h2>
            <button
              type="button"
              className="pro__btn"
              data-tip="Best picks for Red across all its empty roles."
              onClick={() => runTeamSuggest("red")}
            >
              ✦ Best pick
            </button>
          </div>
          {[5, 6, 7, 8, 9].map((slot) => (
            <SlotCard
              key={slot}
              slot={slot}
              champion={board[slot]}
              isTarget={targetSlot === slot}
              banned={allBans.some(
                (b) => b.toLowerCase() === board[slot].trim().toLowerCase(),
              )}
              onChange={(v) => setSlot(slot, v)}
              onSuggest={() => runPickSuggest(slot)}
              champions={champions}
              datalistId="pro-sg-champions"
            />
          ))}
          <BanStrip
            team="red"
            bans={bans.red}
            onRemove={(name) =>
              setBans((prev) => ({
                ...prev,
                red: prev.red.filter((b) => b !== name),
              }))
            }
            onAdd={(name) => addBan("red", name)}
            onSuggest={() => runBanSuggest("red")}
            champions={champions}
            datalistId="pro-sg-champions"
          />
        </div>
      </div>

      <ChampionDatalist id="pro-sg-champions" champions={champions} />
    </div>
  );
}

export default Suggester;
